import { HelpCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * A field's label, with the required marker and the help tooltip.
 *
 * Extracted from `FormRenderer` when `ImageField` became its own module: both
 * render a field and both need this, and having the second import it from the
 * first would have made the pair circular — `FormRenderer` renders `ImageField`.
 * Nothing about it changed in the move.
 *
 * `htmlFor` ties the label to its control so assistive technology reads them
 * together in every label position. `widthClass` is the label column width
 * (z8nrz7djyx); the default is the width every label had before.
 */
export function FieldLabel({
  label,
  required,
  helpText,
  htmlFor,
  widthClass = 'sm:w-36',
  className,
}: {
  label: string
  required?: boolean
  helpText?: string
  htmlFor?: string
  widthClass?: string
  className?: string
}) {
  return (
    <Label htmlFor={htmlFor} className={cn('text-sm sm:shrink-0 flex items-center gap-1', widthClass, className)}>
      <span>
        {label}
        {required && <span className='text-destructive ml-1'>*</span>}
      </span>
      {helpText && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className='h-3.5 w-3.5 shrink-0 text-muted-foreground cursor-help' />
            </TooltipTrigger>
            <TooltipContent side='top' className='max-w-xs'>
              {helpText}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </Label>
  )
}
